import { Link } from "react-router-dom";
import { ChevronRight, ChevronLeft, Phone, Check, DollarSign, ShieldCheck, CalendarCheck, Car, Percent, MapPin, Wrench, Plane, Gauge, Zap, Users, Truck, Snowflake, Star, FileText, AlertTriangle, MessageCircle, Receipt, Ban, TrendingUp, Sparkles, PawPrint, Rocket } from "lucide-react";
import { Seo } from "@/components/Seo";
import { BELO_PROVIDER } from "@/data/cityLocations";
import { HOST_GUIDES, GUIDE_CATEGORIES, GUIDE_PUBLISHED, GUIDE_UPDATED } from "@/data/hostGuides";
import { HOST_GUIDES as G } from "@/data/hostGuides";
import { track } from "@/lib/analytics";

const SITE = "https://belorentals.com";
const ICONS = { DollarSign, ShieldCheck, CalendarCheck, Car, Percent, MapPin, Wrench, Plane, Gauge, Zap, Users, Truck, Snowflake, Star, FileText, AlertTriangle, MessageCircle, Receipt, Ban, TrendingUp, Sparkles, PawPrint, Rocket };

const label = (to) => {
  if (to.startsWith("/host-guides/")) return G[to.replace("/host-guides/", "")]?.h1 || to;
  const map = { "/become-a-host": "Become a Host", "/host-earnings": "Host earnings", "/host-payouts": "Host payouts", "/host-requirements": "Host requirements", "/host-insurance": "Host insurance", "/how-hosting-works": "How hosting works", "/host-faq": "Host FAQ" };
  return map[to] || to;
};

export default function HostGuide({ slug }) {
  const cfg = HOST_GUIDES[slug];
  if (!cfg) return null;
  const Ic = ICONS[cfg.icon] || Car;
  const path = `/host-guides/${slug}`;
  const cat = GUIDE_CATEGORIES.find((c) => c.key === cfg.category);

  const articleLd = { "@context": "https://schema.org", "@type": "Article", headline: cfg.h1, description: cfg.metaDescription,
    author: { "@type": "Organization", name: "Belo Rentals", url: `${SITE}/` }, publisher: { "@type": "Organization", name: "Belo Rentals", logo: { "@type": "ImageObject", url: `${SITE}/belo-favicon.png` } },
    datePublished: GUIDE_PUBLISHED, dateModified: GUIDE_UPDATED,
    mainEntityOfPage: `${SITE}${path}`, image: `${SITE}/belo-og.png` };
  const faqLd = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: cfg.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };
  const crumbLd = { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Host Guides", item: `${SITE}/host-guides` },
    { "@type": "ListItem", position: 3, name: cfg.h1, item: `${SITE}${path}` },
  ] };

  return (
    <div data-testid={`host-guide-${slug}`} className="bg-white">
      <Seo title={cfg.metaTitle} description={cfg.metaDescription} path={path} keywords={cfg.keywords} type="article" jsonLd={[articleLd, faqLd, crumbLd]} />

      <article className="max-w-3xl mx-auto px-5 sm:px-8 pt-32 pb-16">
        <Link to="/host-guides" className="inline-flex items-center gap-1.5 text-blue-600 text-sm font-semibold hover:text-blue-700"><ChevronLeft size={16} /> All host guides</Link>
        <div className="mt-6 flex items-center gap-3">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-blue-600 text-white"><Ic size={20} /></span>
          {cat && <span className="text-sm font-medium text-blue-700 bg-blue-50 px-3 py-1 rounded-full">{cat.label}</span>}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mt-5">{cfg.h1}</h1>
        <p className="mt-3 text-sm text-slate-400" data-testid="guide-byline">By Belo Rentals · Updated {new Date(GUIDE_UPDATED).toLocaleDateString("en-US", { year: "numeric", month: "long" })}</p>
        <p className="mt-4 text-lg text-slate-600 leading-relaxed">{cfg.intro}</p>

        <div className="mt-10 space-y-8">
          {cfg.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-heading text-2xl font-bold text-slate-900">{s.heading}</h2>
              <p className="text-slate-600 mt-3 leading-relaxed">{s.body}</p>
              {s.bullets && (
                <ul className="mt-4 space-y-2">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-slate-600 text-sm leading-relaxed"><Check size={16} className="text-emerald-500 shrink-0 mt-0.5" /> <span>{b}</span></li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        {/* FAQ */}
        <section className="mt-12">
          <h2 className="font-heading text-2xl font-bold text-slate-900">Frequently asked questions</h2>
          <div className="mt-5 space-y-3">
            {cfg.faqs.map((f, i) => (
              <div key={i} data-testid={`guide-faq-${i}`} className="bg-slate-50 rounded-xl border border-slate-100 p-5">
                <p className="font-semibold text-slate-900">{f.q}</p>
                <p className="text-slate-600 text-sm mt-2 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Related */}
        {cfg.related?.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading text-xl font-bold text-slate-900">Keep reading</h2>
            <div className="mt-4 flex flex-wrap gap-3" data-testid="guide-related">
              {cfg.related.map((to) => {
                const href = to.startsWith("/") ? to : `/host-guides/${to}`;
                return <Link key={href} to={href} className="px-4 py-2 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium hover:border-blue-400 hover:text-blue-700 transition-colors">{label(href)}</Link>;
              })}
            </div>
          </section>
        )}

        {/* CTA */}
        <div className="mt-12 rounded-3xl bg-gradient-to-r from-blue-600 to-blue-700 text-white text-center px-8 py-12">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Ready to list your car?</h2>
          <p className="mt-3 text-blue-50 max-w-lg mx-auto text-sm">Set your price and keep your listed rate — no host commission.</p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link to="/host" onClick={() => track("application_started", { page: `guide_${slug}` })} className="px-7 py-3 rounded-full bg-white text-blue-700 font-semibold hover:bg-blue-50 transition-colors inline-flex items-center gap-2">Apply to become a host <ChevronRight size={18} /></Link>
            <a href="tel:+19782211451" className="px-7 py-3 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors inline-flex items-center gap-2"><Phone size={16} /> 978-221-1451</a>
          </div>
        </div>
      </article>
    </div>
  );
}
